/**
 * Budget des charges de l'école (données du serveur) : catégories de charges et charges, avec leur
 * budget mensuel par cycle. Modifier le budget est réservé aux rôles globaux.
 */
import { http, orUndefined } from "./http";
import type { Charge, ChargeCategory } from "@/types";

interface ApiChargeCategory {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  position: number;
}

interface ApiCharge {
  id: number;
  name: string;
  chargeCategoryId: number;
  icon: string | null;
  isActive: boolean;
  oncePerMonth: boolean;
  position: number;
  cycles: Array<{ cycleId: number; categoryId: number; monthly: number }>;
}

function toCategory(c: ApiChargeCategory): ChargeCategory {
  return { id: String(c.id), name: c.name, description: c.description ?? "", icon: c.icon ?? "ri-folder-line" };
}

function toCharge(c: ApiCharge): Charge {
  return {
    id: String(c.id),
    name: c.name,
    categoryId: String(c.chargeCategoryId),
    icon: c.icon ?? "ri-price-tag-3-line",
    amounts: c.cycles.map((cy) => ({ cycleId: String(cy.cycleId), monthly: cy.monthly })),
    isActive: c.isActive,
    oncePerMonth: c.oncePerMonth,
    cycleCategoryIds: Object.fromEntries(c.cycles.map((cy) => [String(cy.cycleId), String(cy.categoryId)])),
  };
}

/* Catégories de charges --------------------------------------------------- */

export async function getChargeCategories(): Promise<ChargeCategory[]> {
  return (await http.get<ApiChargeCategory[]>("/charge-categories")).map(toCategory);
}

export async function addChargeCategory(payload: Omit<ChargeCategory, "id">): Promise<ChargeCategory> {
  return toCategory(
    await http.post<ApiChargeCategory>("/charge-categories", {
      name: payload.name.trim(),
      description: orUndefined(payload.description),
      icon: orUndefined(payload.icon),
    }),
  );
}

export async function updateChargeCategory(id: string, patch: Partial<ChargeCategory>): Promise<ChargeCategory> {
  return toCategory(
    await http.put<ApiChargeCategory>(`/charge-categories/${id}`, {
      name: patch.name?.trim(),
      description: patch.description === undefined ? undefined : patch.description.trim(),
      icon: patch.icon,
    }),
  );
}

export async function deleteChargeCategory(id: string): Promise<void> {
  await http.delete(`/charge-categories/${id}`);
}

/* Charges ------------------------------------------------------------------ */

export async function getCharges(): Promise<Charge[]> {
  return (await http.get<ApiCharge[]>("/charges")).map(toCharge);
}

const budgetsOf = (charge: Partial<Charge>) =>
  charge.amounts?.map((a) => ({ cycleId: Number(a.cycleId), monthly: a.monthly }));

export async function addCharge(payload: Omit<Charge, "id" | "cycleCategoryIds">): Promise<Charge> {
  return toCharge(
    await http.post<ApiCharge>("/charges", {
      name: payload.name.trim(),
      chargeCategoryId: Number(payload.categoryId),
      icon: orUndefined(payload.icon),
      isActive: payload.isActive,
      oncePerMonth: payload.oncePerMonth,
      amounts: budgetsOf(payload) ?? [],
    }),
  );
}

export async function updateCharge(id: string, patch: Partial<Charge>): Promise<Charge> {
  return toCharge(
    await http.put<ApiCharge>(`/charges/${id}`, {
      name: patch.name?.trim(),
      chargeCategoryId: patch.categoryId === undefined ? undefined : Number(patch.categoryId),
      icon: patch.icon,
      isActive: patch.isActive,
      oncePerMonth: patch.oncePerMonth,
      amounts: budgetsOf(patch),
    }),
  );
}

export async function deleteCharge(id: string): Promise<void> {
  await http.delete(`/charges/${id}`);
}
